import type { ColorSwatchGroupProps } from "./color-swatch";

export const FALLBACK_COLOR = "#d8d9d9";

function toSwatchId(name: string): string {
  return name.toLowerCase().replace(/\s+/g, "-");
}

export function resolveSwatchHexColors(color?: {
  colorOneCode?: string;
  colorTwoCode?: string;
}): string[] | undefined {
  if (!color?.colorOneCode) {
    return;
  }

  const colors = [`#${color.colorOneCode}`];
  if (color.colorTwoCode) {
    colors.push(`#${color.colorTwoCode}`);
  }

  return colors;
}

export interface ColorSwatchWithImage {
  id: string;
  label: string;
  colors: string[];
  isFallbackColor?: boolean;
  imageUrl?: string;
  name?: string;
  href?: string;
}

function buildVariantHref(
  productUrl?: string,
  variantCode?: string
): string | undefined {
  if (!(productUrl && variantCode)) {
    return productUrl || undefined;
  }

  const separator = productUrl.includes("?") ? "&" : "?";
  return `${productUrl}${separator}article=${variantCode}`;
}

export interface ColorSwatchGroupWithImagesProps
  extends Omit<ColorSwatchGroupProps, "swatches"> {
  swatches: ColorSwatchWithImage[];
}

export function buildColorSwatches(
  variantProducts: Array<{
    code?: string;
    name?: string;
    color?: { name: string; colorOneCode?: string; colorTwoCode?: string };
    colorNameEn?: string;
    imageUrl?: string;
  }>,
  colorNameMap: Record<string, string[]>,
  selectedColorName?: string,
  productUrl?: string
): ColorSwatchGroupWithImagesProps {
  const addedColorIds = new Set<string>();
  const swatches: ColorSwatchWithImage[] = [];

  for (const variant of variantProducts) {
    if (!variant.color?.name) {
      continue;
    }

    const id = toSwatchId(variant.color.name);

    if (addedColorIds.has(id)) {
      continue;
    }

    addedColorIds.add(id);

    let isFallbackColor: boolean;
    let colors: string[];
    const hexColors = resolveSwatchHexColors(variant.color);
    if (hexColors) {
      colors = hexColors;
      isFallbackColor = false;
    } else {
      colors = colorNameMap[variant.colorNameEn ?? ""] ?? [FALLBACK_COLOR];
      isFallbackColor = true;
    }

    swatches.push({
      id,
      label: variant.color.name,
      colors,
      isFallbackColor,
      imageUrl: variant.imageUrl,
      name: variant.name,
      href: buildVariantHref(productUrl, variant.code),
    });
  }

  return {
    swatches,
    selectedId: selectedColorName ? toSwatchId(selectedColorName) : undefined,
  };
}
